import React, {useState, useEffect} from "react";
import {i18nEffect, translate, getTranslation} from "Core/i18n";
import {app_id, locale_cookie_group, sidebar_menu_shrank_cookie_group} from "Config/app";

/**
 * Cookie policy for en-US locale
 * @note This will automatically be used in CookiePolicy component (index.js) if en-US is the current locale.
 * There is no need to use this component directly.
 *
 * @param {any} props - Any prop that will be added to the DIV element.
 * @return {JSX.Element}
 * @constructor
 */
function CookiePolicyEnUs(props) {
	const i18nEffectHook = i18nEffect.bind(null, useState(getTranslation())[1]);
	useEffect(() => { i18nEffectHook(); }, [i18nEffectHook]);
	
	return (
		<div {...props}>
			<h2>O kolačićima</h2>
			<p>Kada posetite Sleep Profiler portal, prikupljamo informacije neophodne da bismo osigurali da pravilno funkcioniše. Ove tehnologije, koje uključuju kolačiće, piksele, veb bekone i gif-ove, zajednički se nazivaju "kolačići" u ovoj Politici upravljanja kolačićima.</p>
			<p>Informacije o zaštiti ličnih podataka koje obrađuje Sleep Profiler mogu se pronaći u našoj <a href={translate('privacy_policy_link', 'App.info')} target="_blank" rel="noopener noreferrer">Politici privatnosti</a>, a ako imate pitanja u vezi sa obradom ličnih podataka, pošaljite upit na e-mail adresu <a href="mailto:sleepprofiler@advanced-sleep.com">SleepProfiler@advanced-sleep.com</a>.</p>
			<p>Korišćenjem kolačića na našem portalu, možemo prikupljati informacije o vašem računaru, koje mogu uključivati IP adresu, tip pretraživača, naziv domena i vreme pristupa. Kolačići koji su omogućeni na Sleep Profiler sajtu su neophodni za njegovo funkcionalno funkcionisanje.</p>

			<h2>Šta su kolačići?</h2>
			<p>Kolačići su male tekstualne datoteke koje se obično preuzimaju na vaš računar, tablet ili mobilni uređaj (u daljem tekstu: "uređaj") sa veb sajtova koje posećujete. Kolačići se koriste da bi se osiguralo pravilno funkcionisanje veb sajta, pružilo bolje korisničko iskustvo, a takođe i da omoguće prikupljanje podataka o posetama sajtu i performansama i za naprednu onlajn reklamiranje.</p>

			<h2>Vrste kolačića</h2>
			<p>Sleep Profiler portal koristi samo neophodne kolačiće.</p>
			<p>Vrste neophodnih kolačića koje koristimo uključuju:</p>
			<ul>
				<li>
					<strong>Trajni kolačići</strong><br/>
					Trajni kolačići ostaju sačuvani na vašem uređaju čak i nakon što zatvorite internet pretraživač. Uz pomoć ovih kolačića, veb sajtovi čuvaju podatke koji olakšavaju njihovu upotrebu. Na primer, veb sajtovi koji zahtevaju da unesete korisničko ime i lozinku zapamtiće vaš unos koji će se pojaviti kada sledeći put posetite.
				</li>
				<li>
					<strong>Kolačići sesije</strong><br/>
					Kolačići sesije čuvaju privremene podatke koji služe da osiguraju pravilno funkcionisanje Sleep Profiler portala. Kolačići sesije su privremeni i uklanjaju se sa vašeg uređaja kada zatvorite pretraživač nakon posete Sleep Profiler portalu.
				</li>
			</ul>
			<p>NE koristimo kolačiće trećih strana koji bi mogli biti korišćeni u analitičke ili marketinške svrhe.</p>

			<h2>Koje kolačiće koristi Sleep Profiler portal?</h2>
			<p>Kolačići korišćeni na Sleep Profiler portalu su ograničeni na one neophodne za rad sajta.</p>
			<ul>
				<li>Kolačići korišćeni da sajt funkcioniše i ne mogu biti isključeni. Možete podesiti svoj internet pretraživač da vas obavesti o korišćenju.</li>
				<li>Kolačići se koriste za prilagođavanje korisničkog interfejsa kada podesite jezik, veličinu fonta ili određenu verziju sajta (npr. režim visokog kontrasta). Jednom podešeno, ne morate specificirati svoje preferencije sledeći put kada posetite ovaj sajt.</li>
				<li>Kolačići koji vam omogućavaju da odete i vratite se na delove sajta bez ponovne autentifikacije.</li>
			</ul>

			<h2>Specifikacija kolačića</h2>
			<div className="data-table-component standard">
				<table>
					<thead>
						<tr>
							<th className="column"><div className="content">KATEGORIJA KOLAČIĆA</div></th>
							<th className="column" width="1"><div className="content">NAZIV KOLAČIĆA</div></th>
							<th className="column"><div className="content">OPIS KOLAČIĆA</div></th>
						</tr>
					</thead>

					<tbody>
						<tr>
							<td><div className="content">{translate('name', 'CookieSettings.tab.@cookieGroup:necessary')}</div></td>
							<td><div className="content">{app_id}_pauseRequests</div></td>
							<td><div className="content">Kolačić koji se koristi za pauziranje sistemskih IO zahteva</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', 'CookieSettings.tab.@cookieGroup:necessary')}</div></td>
							<td><div className="content">{app_id}_authToken</div></td>
							<td><div className="content">Kolačić koji se koristi za autorizaciju prijavljenog korisnika</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', 'CookieSettings.tab.@cookieGroup:necessary')}</div></td>
							<td><div className="content">{app_id}_authTokenData</div></td>
							<td><div className="content">Kolačić koji sadrži sistemske podatke prijavljenog korisnika</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', 'CookieSettings.tab.@cookieGroup:necessary')}</div></td>
							<td><div className="content">{app_id}_refreshToken</div></td>
							<td><div className="content">Kolačić koji se koristi za autorizaciju prijavljenog korisnika</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', 'CookieSettings.tab.@cookieGroup:necessary')}</div></td>
							<td><div className="content">{app_id}_refreshingToken</div></td>
							<td><div className="content">Kolačić koji se koristi za autorizaciju prijavljenog korisnika</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', 'CookieSettings.tab.@cookieGroup:necessary')}</div></td>
							<td><div className="content">{app_id}_acl</div></td>
							<td><div className="content">Kolačić koji sadrži listu kontrole pristupa (ACL) trenutnog korisnika</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', 'CookieSettings.tab.@cookieGroup:necessary')}</div></td>
							<td><div className="content">{app_id}_currentUser</div></td>
							<td><div className="content">Kolačić koji sadrži sistemske podatke trenutnog korisnika (korisničko ime, tip korisnika, itd.)</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', 'CookieSettings.tab.@cookieGroup:necessary')}</div></td>
							<td><div className="content">{app_id}_userActivity</div></td>
							<td><div className="content">Kolačić koji sadrži podatke o sistemskoj aktivnosti trenutnog korisnika.</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', 'CookieSettings.tab.@cookieGroup:necessary')}</div></td>
							<td><div className="content">{app_id}_cookieConsentSettings</div></td>
							<td><div className="content">Kolačić koji čuva informacije o tome koji kolačići su prihvaćeni od strane korisnika</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', `CookieSettings.tab.@cookieGroup:${locale_cookie_group}`)}</div></td>
							<td><div className="content">{app_id}_locale</div></td>
							<td><div className="content">Kolačić koji sadrži sistemski kod lokalizacije koji se koristi za određivanje jezika koji će biti korišćen</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', `CookieSettings.tab.@cookieGroup:${sidebar_menu_shrank_cookie_group}`)}</div></td>
							<td><div className="content">sidebar_menu_shrank</div></td>
							<td><div className="content">Kolačić koji se koristi za određivanje veličine glavne bočne trake menija</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', 'CookieSettings.tab.@cookieGroup:necessary')}</div></td>
							<td><div className="content">studies_list_advanced_search_visible</div></td>
							<td><div className="content">Kolačić koji se koristi za određivanje da li će biti prikazana napredna bočna traka za pretragu studija</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', 'CookieSettings.tab.@cookieGroup:necessary')}</div></td>
							<td><div className="content">application_parameters</div></td>
							<td><div className="content">Kolačić koji sadrži sadržaj koji će biti prikazan kroz Sleep Profiler Portal kao što su verzija softvera, oznaka autorskih prava, itd.</div></td>
						</tr>
						<tr>
							<td><div className="content">{translate('name', 'CookieSettings.tab.@cookieGroup:necessary')}</div></td>
							<td><div className="content">do_not_show_password_expiration</div></td>
							<td><div className="content">Kolačić koji se koristi za određivanje da li će biti prikazano obaveštenje o isteku lozinke.</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
}

export default CookiePolicyEnUs;